import "../Overview/EndpointOverview.css";
import "../Overview/EndpointAnalytics.css";
import { useEffect, useState } from "react";
import { OverviewDetailsProps } from "../Overview/interfaces/interface";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";
import DashboardAnalyticsFilters from "../Overview/DashboardAnalyticsFilters";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Button } from "../../ui/button";
import { RefreshCw } from "lucide-react";
import { fetchLolbinApi } from "./Api/lolbinapi";
import { mapLolbinActivity, mapLolbinTimeline, mapTopLolbin, mapTopLolbinComputers, mapTopLolbinUsers } from "./helper";
import { LolBinActivityData, LolBinTimelineData, TopLolBinComputerData, TopLolBinData, TopLolBinUserData } from "../interfaces/CommonInterfaces";
import LolbinTimeLine from "./LolbinTimeLine";
import LolbinRecentActivity from "./LolbinRecentActivity";
import LolbinTopUserLbs from "./LolbinTopUserLbs";
import EndpointTimeRangeControls from "../Endpoints/DeviceDetails/EndpointTimeRangeControls";

interface LolbinDetailsProps extends OverviewDetailsProps {
  showTopLolBins?: boolean;
  showTopUsers?: boolean;
  showTopComputers?: boolean;
}

export default function LolbinDetails({
  onSelectorChange,
  fromdetails = false,
  computerName = "",
  computerId = "",
  computeremail = "",
  showTopLolBins = true,
  showTopUsers = true,
  showTopComputers = true,
  controlsContainer,
}: LolbinDetailsProps) {
  const [selectedTime, setSelectedTime] = useState<string>("lw");
  const [selectedTenant, setSelectedTenant] = useState(
    myLocalStorage.getItem("latestTenant")?.tenantName || ""
  );
  const { email } = useUserStore((state) => state.user);
  const tenantName = selectedTenant || myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const [timelineData, setTimelineData] = useState<LolBinTimelineData[]>([]);
  const [activityData, setActivityData] = useState<LolBinActivityData[]>([]);
  const [topLolBin, setTopLolBin] = useState<TopLolBinData[]>([]);
  const [topUsers, setTopUsers] = useState<TopLolBinUserData[]>([]);
  const [topComputers, setTopComputers] = useState<TopLolBinComputerData[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLolbinData = async () => {
    if (!tenantName || !email) {
      return;
    }

    setLoading(true);
    let key = "LolbinKey";
    try {
      const response = await fetchLolbinApi(
        email,
        tenantName,
        "ondevice-auditlog",
        selectedTime,
        computerName,
        computerId,
        key,
        fromdetails,
        computeremail
      );

      if (response) {
        const timeline: LolBinTimelineData[] = mapLolbinTimeline(response);
        const activities: LolBinActivityData[] = mapLolbinActivity(response);
        const topLolbins: TopLolBinData[] = mapTopLolbin(response);
        const topUsers: TopLolBinUserData[] = mapTopLolbinUsers(response);
        const topComputers: TopLolBinComputerData[] = mapTopLolbinComputers(response);


        console.log("Lolbin API Response:", response);

        setTimelineData(timeline);
        setActivityData(activities);
        setTopLolBin(topLolbins);
        setTopUsers(topUsers);
        setTopComputers(topComputers);


      } else {
        console.log("No data received");
      }
    } catch (error) {
      console.error("Error fetching overview data:", error);
    }
    setLoading(false);
  }

  const handleRefresh = () => {
    void fetchLolbinData();
  };


  useEffect(() => {
    void fetchLolbinData();
  }, [selectedTime, tenantName, email, computerName, computerId, computeremail, fromdetails]);

  const hasTopSections = showTopLolBins || showTopUsers || showTopComputers;

  return (<>
    <div className={fromdetails ? "endpoint-overview endpoint-lolbin" : "endpoint-overview endpoint-lolbin dashboard-analytics dashboard-lolbin"}>
      {/* Tenant selector */}
      {!fromdetails ? (
        <DashboardAnalyticsFilters
          onTenantChange={(value: string) => {
            setSelectedTenant(value);
            onSelectorChange?.(value);
          }}
          timeRange={selectedTime}
          onTimeRangeChange={setSelectedTime}
          onRefresh={handleRefresh}
          refreshDisabled={loading}
        />
      ) : controlsContainer ? (
        <EndpointTimeRangeControls
          container={controlsContainer}
          value={selectedTime}
          onValueChange={setSelectedTime}
          onRefresh={handleRefresh}
          refreshDisabled={loading}
        />
      ) : <div className="flex items-center justify-between gap-2">
        <div className="relative">
          <label
            htmlFor="time-range-selector"
            className="absolute -top-4 left-3 z-10 px-1 text-xs font-medium text-muted-foreground"
          >
            Time Range
          </label>
          <Select value={selectedTime} onValueChange={setSelectedTime}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24Hrs">Last 24 Hours</SelectItem>
              <SelectItem value="lw">Last 7 Days</SelectItem>
              <SelectItem value="lm">Last 30 Days</SelectItem>
              <SelectItem value="l3m">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>}

      {/* Grid layout for the content below */}
      <div className="analytics-lolbin-grid">
        {/* Content on the left */}
        <div className="analytics-lolbin-summary">
          <LolbinTimeLine fromdetails timelineData={timelineData} loading={loading} />
          {hasTopSections && (
            <LolbinTopUserLbs
              fromdetails
              topUsers={topUsers}
              topLolBin={topLolBin}
              topComputers={topComputers}
              loading={loading}
              showTopLolBins={showTopLolBins}
              showTopUsers={showTopUsers}
              showTopComputers={showTopComputers}
            />
          )}
        </div>

        {/* Content on the right */}
        <div>
          <LolbinRecentActivity fromdetails activityData={activityData} loading={loading} />
        </div>
      </div>
    </div>

  </>);
}
